import * as Sentry from '@sentry/react';

// Limite defensivo para o data URL do screenshot embutido no evento. O
// @sentry/react 6.x nao suporta attachments nativos, entao o print vai como
// extra (data URL). Mantido pequeno para nao estourar o budget do evento.
const MAX_SCREENSHOT_DATA_URL = 200 * 1024;

// html2canvas e carregado sob demanda via CDN (nao e dependencia do bundle),
// de forma best-effort: se nao carregar, o erro e reportado sem o print.
const HTML2CANVAS_SRC =
  'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
let html2canvasPromise = null;

function loadHtml2Canvas() {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  if (window.html2canvas) {
    return Promise.resolve(window.html2canvas);
  }
  if (html2canvasPromise) {
    return html2canvasPromise;
  }
  html2canvasPromise = new Promise((resolve) => {
    try {
      const script = document.createElement('script');
      script.src = HTML2CANVAS_SRC;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => resolve(window.html2canvas || null);
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    } catch (e) {
      resolve(null);
    }
  });
  return html2canvasPromise;
}

// Define (ou limpa) o usuario corrente para todos os eventos do GlitchTip.
export function setGlitchtipUser(email) {
  try {
    Sentry.setUser(email ? { email } : null);
  } catch (e) {
    /* best-effort */
  }
}

// Captura best-effort da tela atual como JPEG compacto (data URL) ou null.
async function captureScreenshot() {
  try {
    const html2canvas = await loadHtml2Canvas();
    if (!html2canvas) {
      return null;
    }
    const canvas = await html2canvas(document.body, {
      logging: false,
      scale: 0.4,
      useCORS: true,
      backgroundColor: '#ffffff',
      ignoreElements: (el) => el.tagName === 'IFRAME' || el.tagName === 'SCRIPT',
    });
    const dataUrl = canvas.toDataURL('image/jpeg', 0.5);
    return dataUrl && dataUrl.length <= MAX_SCREENSHOT_DATA_URL ? dataUrl : null;
  } catch (e) {
    return null;
  }
}

// Reporta a falha de uma operacao ao GlitchTip com contexto suficiente para
// reproduzir o problema (side, fluxo, endpoint, payload, resposta) e, quando
// possivel, o print da tela. Retorna o eventId (referencia de suporte) ou null.
export async function reportApiError(error, context = {}) {
  const {
    flow = 'unknown',
    endpoint,
    payload,
    unexpected = true,
  } = context;

  let screenshot = null;
  try {
    screenshot = await captureScreenshot();
  } catch (e) {
    screenshot = null;
  }

  let eventId = null;
  try {
    Sentry.withScope((scope) => {
      scope.setLevel(unexpected ? 'error' : 'warning');
      scope.setTag('side', 'front');
      scope.setTag('flow', flow);
      scope.setTag('unexpected', unexpected ? 'true' : 'false');
      scope.setExtra('endpoint', endpoint);
      scope.setExtra('httpStatus', error && error.response && error.response.status);
      scope.setExtra('backendResponse', error && error.response && error.response.data);
      scope.setExtra('payload', payload);
      if (screenshot) {
        scope.setTag('has_screenshot', 'true');
        scope.setExtra('screenshotDataUrl', screenshot);
      }
      eventId = Sentry.captureException(error);
    });
  } catch (e) {
    try {
      eventId = Sentry.captureException(error);
    } catch (_) {
      eventId = null;
    }
  }
  return eventId;
}
